import { Modal, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { Fragment, useState } from 'react';

import { EditOrganizationForm } from '@/components/management/form/EditOrganizationForm';
import { InviteOrganizationUserForm } from '@/components/management/form/InviteOrganizationUserForm';
import { StyledModal } from '@/components/management/form/formHelpers/StyledModal';
import { StyledButton } from '@/components/management/formElements/StyledButton';
import { OrganizationUserTable } from '@/components/management/table/OrganizationUserTable';
import type { Organization, UpdateOrganizationDto } from '@/modules/api/backend';
import { backendApiOrganizationsCreateOrganizationUserMutation } from '@/modules/api/backend/@tanstack/react-query.gen';
import { useSnackbarStore } from '@/stores/useSnackbarStore';

interface OrganizationFormWrapperProps {
  organizationData: Organization;
  setRef: (idx: number) => (ref: HTMLDivElement | null) => void;
  tabs: string[];
  onSubmit: (values: UpdateOrganizationDto) => Promise<void>;
}

export function EditOrganizationFormWrapper({
  organizationData,
  setRef,
  tabs,
  onSubmit,
}: OrganizationFormWrapperProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const { openSnackbar } = useSnackbarStore((state) => state.actions);

  const { mutateAsync } = useMutation({
    ...backendApiOrganizationsCreateOrganizationUserMutation(),
    onSuccess: () => {
      openSnackbar({
        color: 'success',
        message: 'Die Einladung wurde erfolgreich versendet!',
      });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      if (error?.message?.[0]?.constraints?.isUnique) {
        openSnackbar({
          color: 'error',
          message: 'Diese E-Mail-Adresse ist bereits registriert.',
        });
      } else {
        openSnackbar({
          color: 'error',
          message: 'Fehler beim Versenden der Einladung.',
        });
      }
      setModalOpen(false);
    },
  });

  return (
    <Fragment>
      <Stack gap={4}>
        <EditOrganizationForm initialValues={organizationData} onSubmit={onSubmit} setRef={setRef} tabs={tabs} />
        <Stack>
          <Typography ref={setRef(1)} gutterBottom variant="h2">
            User*innen
          </Typography>
          {organizationData.users ? <OrganizationUserTable organizationUserData={organizationData.users} /> : null}
        </Stack>

        <Stack alignItems="flex-end">
          <StyledButton label="User*in hinzufügen" onClick={() => setModalOpen(true)} />
        </Stack>
      </Stack>

      <Modal aria-labelledby="User*in hinzufügen" onClose={() => setModalOpen(false)} open={modalOpen}>
        <StyledModal>
          <InviteOrganizationUserForm
            onCancel={() => setModalOpen(false)}
            onSubmit={async (values) => {
              await mutateAsync({
                path: { organizationId: organizationData.id },
                body: values,
              });
              setModalOpen(false);
            }}
          />
        </StyledModal>
      </Modal>
    </Fragment>
  );
}
